import React, {useMemo, useState, useEffect, forwardRef, useRef, ComponentRef} from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {debounce} from '../../../domain/lib/debounce';
import settingsParams from '../../../components/cart/settingsParams';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import {TextArea, TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import s from './BuyerNote.scss';
import {classes} from './BuyerNote.st.css';
import {useSettingsWithDefaults} from '../../../hooks/useSettingsWithDefaults';
import {Document} from '@wix/wix-ui-icons-common';
import type {ISettingsParam} from '@wix/tpa-settings';

export enum BuyerNoteDataHook {
  Root = 'BuyerNoteDataHook.root',
  Button = 'BuyerNoteDataHook.button',
  Body = 'BuyerNoteDataHook.body',
}

export interface BuyerNoteProps {
  buttonCaptionSettingKey?: ISettingsParam;
  buttonPlaceholderSettingKey?: ISettingsParam;
}

const AddNoteButton = ({handleClick, caption, isOpen}: {handleClick: () => void; caption: string; isOpen: boolean}) => {
  const {sideCartNote} = useControllerProps().cartStore;

  return (
    <TextButton
      data-hook={BuyerNoteDataHook.Button}
      priority={TextButtonPriority.primary}
      className={sideCartNote ? classes.textButton : classes.textButtonOld}
      prefixIcon={<Document />}
      aria-expanded={isOpen}
      onClick={handleClick}>
      {caption}
    </TextButton>
  );
};

const AddNoteBody = forwardRef(
  (
    {
      placeholder,
      handleChange,
      onBlur,
      onFocus,
      buyerNote,
    }: {
      placeholder: string;
      handleChange: any;
      onBlur: () => void;
      onFocus: () => void;
      buyerNote: string;
    },
    ref: React.Ref<ComponentRef<typeof TextArea>>
  ) => {
    const {t} = useTranslation();
    const {sideCartNote} = useControllerProps().cartStore;

    return (
      <div className={s.body}>
        <TextArea
          ref={ref}
          className={sideCartNote ? classes.textArea : classes.textAreaOld}
          data-hook={BuyerNoteDataHook.Body}
          placeholder={placeholder}
          maxLength={250}
          value={buyerNote || ''}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          ariaLabel={t('cart.buyer_note_placeholder')}
        />
      </div>
    );
  }
);

AddNoteBody.displayName = 'AddNoteBody';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const BuyerNote = ({buttonCaptionSettingKey, buttonPlaceholderSettingKey}: BuyerNoteProps) => {
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {updateBuyerNote, sendAddNoteBi, sideCartNote, cartModel} = useControllerProps().cartStore;
  const [buyerNote, setBuyerNote] = useState<string>(cartModel.note);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(cartModel.note));
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef<ComponentRef<typeof TextArea>>(null);
  const debouncedUpdateBuyerNote = useMemo(
    () => debounce(updateBuyerNote, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      const shouldUpdate = sideCartNote && !isFocused;

      if (shouldUpdate) {
        setBuyerNote(cartModel.note);
        if (cartModel.note && !isOpen) {
          setIsOpen(true);
        }
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ [cartModel.note, isFocused]
  );

  const handleChange = ({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBuyerNote(value);
    return debouncedUpdateBuyerNote(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleOpen = () => {
    if (!isOpen) {
      setTimeout(() => ref.current?.focus(), 0);
      sendAddNoteBi();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.note} data-hook={BuyerNoteDataHook.Root}>
      <AddNoteButton
        isOpen={isOpen}
        handleClick={handleOpen}
        caption={
          sideCartNote
            ? getSettingsWithDefaults(buttonCaptionSettingKey)
            : getSettingsWithDefaults(settingsParams.CART_ADD_NOTE_LINK)
        }
      />
      {isOpen && (
        <AddNoteBody
          placeholder={
            sideCartNote
              ? getSettingsWithDefaults(buttonPlaceholderSettingKey)
              : getSettingsWithDefaults(settingsParams.CART_INSTRUCTIONS_TEXT)
          }
          handleChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          buyerNote={buyerNote}
          ref={ref}
        />
      )}
    </div>
  );
};
